import { useEffect, useState } from "react";

/**
 * Subscribe to a CSS media query from React.
 *
 * Why this exists: some components need to render genuinely DIFFERENT markup
 * per breakpoint rather than the same markup styled differently. The FAQ is the
 * case that forced it - on phones the answers must be present in the document
 * with no disclosure widget at all, while desktop keeps the accordion. Doing
 * that with `hidden md:block` would put BOTH copies in the DOM, so screen
 * readers would read every answer twice and any analytics inside them would
 * double-fire. Rendering one variant keeps the document honest.
 *
 * Safe before hydration: `window` is only touched inside the initialiser and
 * the effect, and the initial value is read synchronously so the first paint is
 * already correct (no flash of the wrong layout).
 */
export default function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const list = window.matchMedia(query);
    const onChange = (event) => setMatches(event.matches);
    setMatches(list.matches);
    // addEventListener is the modern API; addListener is kept for older
    // WebKit builds still common on long-lived Android devices.
    if (list.addEventListener) {
      list.addEventListener("change", onChange);
      return () => list.removeEventListener("change", onChange);
    }
    list.addListener(onChange);
    return () => list.removeListener(onChange);
  }, [query]);

  return matches;
}

/** Tailwind's `md` breakpoint, so JS and CSS agree on what "desktop" means. */
export const DESKTOP_QUERY = "(min-width: 768px)";
